// Retour immédiat au clic sur un lien de la landing.
//
// Une navigation Turbo laisse l'écran inchangé jusqu'à l'arrivée de la réponse (la transition de
// vue ne démarre qu'à ce moment-là) : sur une connexion ou un serveur lents, le clic semble n'avoir
// rien fait. Les pages de la landing n'ont aucune donnée à attendre, donc pas de squelette — mais
// l'écran doit répondre dans la même image que le clic :
//
//   - mousedown / toucher : l'état `:active` des liens (CSS, cf. config/landing_pages.scss), qui ne
//     passe par aucun JS et se voit avant même que le clic n'existe ;
//   - turbo:click : le lien cliqué reste en évidence et le contenu de la page s'estompe, ce qui se
//     lit comme « la page s'en va ».
//
// L'état est posé sur <html> et pas sur <body> : Turbo remplace le body au rendu, <html> survit.
// Il est donc retiré à `turbo:render`, qui part DANS la transition de vue : l'ancienne page y est
// déjà capturée estompée, la nouvelle ne l'est jamais, et le fondu enchaîné part d'où l'on en est
// au lieu de repasser par la pleine opacité.

const LEAVING_CLASS = 'landing-leaving';
const PRESSED_CLASS = 'landing-link-pressed';
// Filet : une visite annulée ou en erreur ne doit pas laisser la page estompée.
const SAFETY_TIMEOUT_MS = 6000;

let safetyTimeout: number | null = null;

const reset = () => {
  if (safetyTimeout !== null) {
    window.clearTimeout(safetyTimeout);
    safetyTimeout = null;
  }

  document.documentElement.classList.remove(LEAVING_CLASS);
  document.querySelectorAll(`.${PRESSED_CLASS}`).forEach((link) => link.classList.remove(PRESSED_CLASS));
};

// Un lien d'ancre vers la page courante ne quitte rien : pas d'estompage, la page ne se recharge pas.
const leavesThePage = (link: HTMLAnchorElement) =>
  link.pathname !== window.location.pathname || link.search !== window.location.search;

export function initializeLandingClickFeedback() {
  document.addEventListener('turbo:click', (event) => {
    if (!document.body.classList.contains('landing-layout')) return;

    const link = (event.target as Element | null)?.closest<HTMLAnchorElement>('a[href]');
    if (!link || !leavesThePage(link)) return;

    link.classList.add(PRESSED_CLASS);
    document.documentElement.classList.add(LEAVING_CLASS);

    if (safetyTimeout !== null) window.clearTimeout(safetyTimeout);
    safetyTimeout = window.setTimeout(reset, SAFETY_TIMEOUT_MS);
  });

  ['turbo:render', 'turbo:load', 'turbo:fetch-request-error'].forEach((name) => {
    document.addEventListener(name, reset);
  });

  // Retour arrière servi par le cache du navigateur : le document est réaffiché tel qu'on l'a quitté.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) reset();
  });
}
